// web/src/shell/MobileGate.tsx
//
// Wraps a desktop-only route. On viewports <= 768px, shows a full-screen
// "Tapelens works best on desktop" interstitial in place of the wrapped
// children. The user can choose to continue anyway; that choice is
// persisted to localStorage so subsequent visits skip the interstitial.
//
// The trading view's polling/keyboard hooks do NOT mount while the
// interstitial is up — children are conditionally rendered, not just
// hidden — so we don't burn the rate limit on a screen the user can't
// usefully see.
import { useState, type ReactNode } from "react";
import { MobileInterstitial } from "./MobileInterstitial";
import { useIsMobileViewport } from "./useIsMobileViewport";

const STORAGE_KEY = "tapelens.mobile.continue";

function readContinued(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeContinued() {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    /* sandbox / private mode — silently noop, the prompt comes back next visit */
  }
}

export function MobileGate({ children }: { children: ReactNode }) {
  const isMobile = useIsMobileViewport();
  const [continued, setContinued] = useState<boolean>(readContinued);

  if (!isMobile || continued) return <>{children}</>;

  return (
    <MobileInterstitial
      onContinue={() => {
        writeContinued();
        setContinued(true);
      }}
    />
  );
}
